import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Save } from "lucide-react";
import { cmsApi } from "@/services/api/cmsApi";
import WysiwygEditor from "@/components/documents/FormBuilder/WysiwygEditor";
import type { CMSContent, CMSContentTranslation, CMSLanguage } from "@/models/CMS";
import { Textarea } from "@/components/ui/textarea";

interface ContentEditorProps {
  contentId: number;
}

export default function ContentEditor({
  contentId
}: ContentEditorProps) {
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("content");
  const [pendingChanges, setPendingChanges] = useState<Record<string, Record<string, any>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [savedContent, setSavedContent] = useState<Record<string, Record<string, any>>>({});
  const {
    data: content,
    isLoading: isLoadingContent
  } = useQuery({
    queryKey: ['cms-content', contentId],
    queryFn: () => cmsApi.getContent(contentId)
  });
  const {
    data: translations,
    isLoading: isLoadingTranslations
  } = useQuery({
    queryKey: ['cms-translations', contentId],
    queryFn: () => cmsApi.getContentTranslations(contentId)
  });
  const {
    data: languages
  } = useQuery({
    queryKey: ['cms-languages'],
    queryFn: cmsApi.getLanguages
  });

  useEffect(() => {
    if (translations) {
      const contentMap: Record<string, Record<string, any>> = {};
      translations.forEach(translation => {
        contentMap[translation.language_code] = {
          ...translation
        };
      });
      setSavedContent(contentMap);
    }
  }, [translations]);

  const updateTranslation = useMutation({
    mutationFn: ({
      languageCode,
      data
    }: {
      languageCode: string;
      data: Partial<CMSContentTranslation>;
    }) => {
      console.log(`Sending mutation for language ${languageCode} with data:`, data);
      return cmsApi.upsertTranslation(contentId, languageCode, data);
    },
    onSuccess: (response, variables) => {
      console.log(`Mutation success for language ${variables.languageCode}`, response);
      setSavedContent(prev => ({
        ...prev,
        [variables.languageCode]: {
          ...(prev[variables.languageCode] || {}),
          ...variables.data
        }
      }));
      queryClient.setQueryData(['cms-translations', contentId], (oldData: CMSContentTranslation[] | undefined) => {
        if (!oldData) return [response];
        const existingIndex = oldData.findIndex(item => item.language_code === variables.languageCode);
        if (existingIndex >= 0) {
          const updatedTranslations = [...oldData];
          updatedTranslations[existingIndex] = {
            ...updatedTranslations[existingIndex],
            ...variables.data
          };
          return updatedTranslations;
        } else {
          return [...oldData, response];
        }
      });
      if (variables.data.title && languages && languages.length > 0) {
        const defaultLang = languages.find(l => l.is_default);
        if (defaultLang && variables.languageCode === defaultLang.code) {
          updateContentName.mutate(variables.data.title);
        }
      }
    },
    onError: error => {
      console.error('Translation update error:', error);
      toast({
        title: "Error",
        description: `Failed to update content: ${error}`,
        variant: "destructive"
      });
    }
  });

  const updateContentName = useMutation({
    mutationFn: (name: string) => {
      console.log(`Updating content name to: ${name}`);
      return cmsApi.updateContentName(contentId, name);
    },
    onSuccess: updatedContent => {
      console.log('Content name updated successfully:', updatedContent);
      queryClient.setQueryData(['cms-content', contentId], updatedContent);
      queryClient.invalidateQueries({
        queryKey: ['cms-content-tree']
      });
    },
    onError: error => {
      console.error('Error updating content name:', error);
    }
  });

  const updateContentMutation = useMutation({
    mutationFn: (data: Partial<CMSContent>) => cmsApi.updateContent(contentId, data),
    onSuccess: updatedContent => {
      queryClient.setQueryData(['cms-content', contentId], updatedContent);
      queryClient.invalidateQueries({
        queryKey: ['cms-content-tree']
      });
      toast({
        title: "Success",
        description: "Content updated successfully"
      });
    },
    onError: error => {
      toast({
        title: "Error",
        description: `Failed to update content: ${error}`,
        variant: "destructive"
      });
    }
  });

  const handleShowInMenuChange = (checked: boolean) => {
    updateContentMutation.mutate({
      show_in_menu: checked
    });
  };

  const getTranslationByLanguage = (languageCode: string) => {
    if (!translations || !Array.isArray(translations)) return null;
    return translations.find(t => t.language_code === languageCode);
  };

  const handleFieldChange = (languageCode: string, field: string, value: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [languageCode]: {
        ...(prev[languageCode] || {}),
        [field]: value
      }
    }));
  };

  const getFieldValue = (languageCode: string, field: string) => {
    if (pendingChanges[languageCode]?.[field] !== undefined) {
      return pendingChanges[languageCode][field];
    }
    if (savedContent[languageCode]?.[field] !== undefined) {
      return savedContent[languageCode][field];
    }
    const translation = getTranslationByLanguage(languageCode);
    return translation?.[field as keyof CMSContentTranslation] || '';
  };

  const saveAllChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    setIsSaving(true);
    try {
      for (const languageCode of Object.keys(pendingChanges)) {
        const changes = pendingChanges[languageCode];
        console.log(`Saving changes for language ${languageCode}:`, changes);
        if (Object.keys(changes).length > 0) {
          await updateTranslation.mutateAsync({
            languageCode,
            data: changes
          });
          console.log(`Successfully saved changes for language ${languageCode}`);
        }
      }
      setPendingChanges({});
      toast({
        title: "Success",
        description: "Content updated successfully"
      });
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;
  const isNavigationType = content?.type === 'navigation';

  const renderSlugInput = (languageCode: string) => {
    const parentSlug = content?.parent_id ? translations?.find(t => 
      t.content_id === content.parent_id && t.language_code === languageCode
    )?.slug || '' : '';

    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">Slug</label>
        <div className="flex items-center gap-2">
          {parentSlug && (
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {parentSlug}/
            </span>
          )}
          <Input 
            placeholder="URL Slug" 
            value={getFieldValue(languageCode, 'slug')?.replace(`${parentSlug}/`, '')} 
            onChange={e => {
              const newSlug = e.target.value.replace(/^\/+|\/+$/g, '');
              handleFieldChange(languageCode, 'slug', newSlug);
            }}
            className="max-w-md" 
          />
        </div>
      </div>
    );
  };

  if (isLoadingContent || isLoadingTranslations) {
    return <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>;
  }

  return <div className="flex-1 p-6 overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="flex items-center space-x-2">
            <Checkbox id="show-in-menu" checked={content?.show_in_menu || false} onCheckedChange={handleShowInMenuChange} />
            <label htmlFor="show-in-menu" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Show in Menu
            </label>
          </div>
          
          <Button onClick={saveAllChanges} disabled={!hasPendingChanges || isSaving} className="ml-4">
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>
      
      {isNavigationType ? (
        <div className="space-y-6">
          {languages?.map(language => (
            <div key={language.code} className="space-y-4 p-4 rounded-lg border bg-card">
              <h3 className="text-lg font-medium">{language.name}</h3>
              <div className="space-y-2">
                <label className="text-sm font-medium">Title</label>
                <Input placeholder="Navigation Title" value={getFieldValue(language.code, 'title')} onChange={e => handleFieldChange(language.code, 'title', e.target.value)} />
              </div>
              {renderSlugInput(language.code)}
            </div>
          ))}
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="content">Content</TabsTrigger>
            {content?.type === 'page' && <TabsTrigger value="seo">SEO</TabsTrigger>}
          </TabsList>

          <TabsContent value="content" className="space-y-6">
            {languages?.map(language => (
              <div key={language.code} className="space-y-4 p-4 rounded-lg border bg-card">
                <h3 className="text-lg font-medium">{language.name}</h3>
                
                {content?.type === 'page' && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Title</label>
                      <Input placeholder="Page Title" value={getFieldValue(language.code, 'title')} onChange={e => handleFieldChange(language.code, 'title', e.target.value)} />
                    </div>
                    {renderSlugInput(language.code)}
                  </>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Content</label>
                  <WysiwygEditor value={getFieldValue(language.code, 'content')} onChange={html => handleFieldChange(language.code, 'content', html)} />
                </div>
              </div>
            ))}
          </TabsContent>

          {content?.type === 'page' && (
            <TabsContent value="seo" className="space-y-6">
              {languages?.map(language => (
                <div key={language.code} className="space-y-4 p-4 rounded-lg border bg-card">
                  <h3 className="text-lg font-medium">{language.name} - SEO</h3>
                  {renderSlugInput(language.code)}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Meta Description</label>
                    <Textarea placeholder="Meta Description" className="min-h-[100px]" value={getFieldValue(language.code, 'meta_description')} onChange={e => handleFieldChange(language.code, 'meta_description', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Meta Keywords</label>
                    <Input placeholder="Comma-separated keywords" value={getFieldValue(language.code, 'meta_keywords')} onChange={e => handleFieldChange(language.code, 'meta_keywords', e.target.value)} />
                  </div>
                </div>
              ))}
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>;
}
